/**
 * SEARCH PLAYBOOK — the Google Ads Search (Leads + Website traffic)
 * assistant's frontend half. Talks only to /google-ads/search/ai/* and maps
 * the draft onto SearchCampaignWizard's form state. A sibling of the App
 * Promotion playbook — it imports nothing from it.
 */
import type { AiAssistantPlaybook, AssistantDraftRow } from '../types';
import {
  googleAdsService, GoogleAdsGeoSuggestion, GoogleAdsLanguage, SearchAiDraft, SearchAiSite, SearchBidding, SearchObjectiveKey,
} from '@/services/google-ads.service';
import { groupFromAi, SearchGroupDraft, biddingTitle } from '@/components/google-ads/search/searchShared';

/** Fields the assistant may fill; the wizard shows an "AI suggested" badge on each until the user edits it. */
export type SearchAiField = 'website' | 'name' | 'bidding' | 'budget' | 'dates' | 'locations' | 'languages' | 'networks' | 'groups';

/** The slice of SearchCampaignWizard state the playbook writes to. */
export interface SearchAiForm {
  setWebsite: (v: string) => void;
  setName: (v: string) => void;
  setBidding: (v: SearchBidding) => void;
  setBudget: (v: string) => void;
  setStart: (v: string) => void;
  setEnd: (v: string) => void;
  setLocations: (v: GoogleAdsGeoSuggestion[]) => void;
  setLanguages: (v: GoogleAdsLanguage[]) => void;
  setSearchPartners: (v: boolean) => void;
  setDisplayNetwork: (v: boolean) => void;
  setGroups: (v: SearchGroupDraft[]) => void;
  markAi: (fields: SearchAiField[]) => void;
  /** Keep the interview so the step-2 "Write with AI" / "Suggest" buttons reuse the session and the site. */
  setAiContext: (ctx: { sessionId: string; site: SearchAiSite }) => void;
}

const fromMicros = (m: number) => {
  const v = m / 1_000_000;
  return Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/\.?0+$/, '');
};

export function createSearchPlaybook(opts: { accountUid: string; currency?: string; objective: SearchObjectiveKey }): AiAssistantPlaybook<SearchAiSite, SearchAiDraft, SearchAiForm> {
  const cur = opts.currency ? ` ${opts.currency}` : '';
  const leads = opts.objective === 'leads';
  return {
    id: `google-ads.search.${opts.objective}`,
    title: 'Start with AI',
    description: leads
      ? 'Paste your website, answer four quick questions, get a complete Leads campaign — keywords and ads included.'
      : 'Paste your website, answer four quick questions, get a complete Website traffic campaign — keywords and ads included.',
    entry: {
      label: 'Your website',
      placeholder: 'https://yourbusiness.com',
      hint: 'The site is read publicly — services, pages and contact options. Nothing is published until you do it.',
      buttonLabel: 'Read',
    },
    steps: ['Website', 'Offer', 'Locations', 'Budget', 'Language', 'Draft'],
    applyLabel: 'Apply to form',

    async start(input) {
      const r = await googleAdsService.searchAi.start(input, opts.objective);
      return { sessionId: r.sessionId, subject: r.site, question: r.question };
    },

    async answer(sessionId, answer) {
      return googleAdsService.searchAi.answer(sessionId, answer);
    },

    async draft(sessionId) {
      return googleAdsService.searchAi.draft({ sessionId, accountUid: opts.accountUid, currency: opts.currency });
    },

    describeSubject(site) {
      const badges: string[] = [];
      if (site.readError) badges.push('Could not read the site');
      if (site.language) badges.push(site.language.toUpperCase());
      if (site.phones.length) badges.push('Phone on site');
      if (site.whatsapp) badges.push('WhatsApp');
      if (site.links.length) badges.push(`${site.links.length} pages`);
      return {
        title: site.siteName ?? site.host,
        subtitle: site.title && site.title !== site.siteName ? `${site.host} · ${site.title}` : site.host,
        imageUrl: site.iconUrl ?? undefined,
        badges,
        description: site.readError
          ? `${site.readError}. No problem — the draft will be written from your answers instead.`
          : site.description ?? undefined,
      };
    },

    summarizeDraft(d): AssistantDraftRow[] {
      const c = d.campaign;
      const r = d.rationale;
      const kw = d.groups.reduce((n, g) => n + g.keywords.length, 0);
      return [
        { key: 'name', label: 'Campaign name', value: c.name },
        { key: 'bidding', label: 'Bidding', value: biddingTitle(c.bidding), rationale: r.bidding },
        { key: 'budget', label: 'Daily budget', value: `${fromMicros(c.dailyBudgetMicros)}${cur}`, rationale: r.budget },
        { key: 'dates', label: 'Schedule', value: c.endDate ? `When enabled → ${c.endDate}` : 'When enabled · no end date', rationale: r.schedule },
        { key: 'geo', label: 'Locations', value: d.targeting.geo.length ? d.targeting.geo.map((g) => g.name).join(', ') : 'Anywhere', rationale: r.locations },
        { key: 'lang', label: 'Ad language', value: d.targeting.languages.map((l) => l.name).join(', ') || 'Matched from the ad text', rationale: r.languages },
        { key: 'groups', label: 'Ad groups', value: d.groups.map((g) => `${g.name} (${g.keywords.length})`).join(' · '), rationale: r.structure },
        { key: 'keywords', label: 'Keywords', value: `${kw} keywords · e.g. ${d.groups[0]?.keywords.slice(0, 3).map((k) => k.text).join(', ') ?? ''}`, rationale: r.keywords },
        { key: 'ads', label: 'Ads', value: `${d.groups.length} ad${d.groups.length === 1 ? '' : 's'} · e.g. "${d.groups[0]?.ad.headlines[0] ?? ''}"`, rationale: r.copy },
      ];
    },

    draftWarnings(d) {
      const w: string[] = [];
      if (d.targeting.unresolved.length) {
        w.push(`Google could not match ${d.targeting.unresolved.join(', ')} to a location — add ${d.targeting.unresolved.length === 1 ? 'it' : 'them'} in Locations by hand.`);
      }
      if (d.groups.some((g) => g.ad.headlines.length < 10)) w.push('Some ads have fewer than 10 headlines — add more in step 2 for a stronger ad.');
      return w;
    },

    applyDraft(d, form, ctx) {
      const c = d.campaign;
      form.setWebsite(ctx.subject.url);
      form.setName(c.name);
      form.setBidding(c.bidding);
      form.setBudget(fromMicros(c.dailyBudgetMicros));
      form.setStart(c.startDate);
      form.setEnd(c.endDate ?? '');
      form.setLocations(d.targeting.geo.map((g) => ({ id: g.id, name: g.name, canonicalName: g.canonicalName, countryCode: g.countryCode, targetType: g.targetType })));
      form.setLanguages(d.targeting.languages.map((l) => ({ id: l.id, code: l.code, name: l.name, targetable: true })));
      form.setSearchPartners(c.searchPartners);
      form.setDisplayNetwork(c.displayNetwork);
      form.setGroups(d.groups.map((g, i) => groupFromAi(g, i + 1)));
      form.markAi(['website', 'name', 'bidding', 'budget', 'dates', 'locations', 'languages', 'networks', 'groups']);
      form.setAiContext({ sessionId: ctx.sessionId, site: ctx.subject });
    },
  };
}
